import { apiClient } from "@/lib/api";

export type SeasonStatus = "upcoming" | "live" | "ended" | "unset";

export interface EpisodeBrief {
  endsAt: string;
  id: string;
  showName: string;
  startsAt: string;
  title: string;
}

export interface MonthCount {
  month: string;
  recorded: number;
  unrecorded: number;
}

export interface ShowCount {
  episodes: number;
  showId: string;
  showName: string;
}

export interface SlotCount {
  episodes: number;
  hour: number;
  weekday: number;
}

export interface Activity {
  actorEmail: string | null;
  actorType: string;
  createdAt: string;
  entityType: string;
  id: string;
  operation: string;
  summary: string | null;
}

export interface DashboardStats {
  activity: Activity[] | null;
  missingRecordings: { episodes: EpisodeBrief[]; total: number };
  months: MonthCount[];
  season: {
    endsAt: string;
    id: string | null;
    startsAt: string;
    status: SeasonStatus;
  };
  slots: SlotCount[];
  summary: {
    aired: number;
    contributors: number;
    pollResponses: number;
    polls: number;
    recorded: number;
    scheduled: number;
    shows: number;
  };
  topShows: ShowCount[];
  upcoming: EpisodeBrief[];
}

export const getDashboardStats = async (
  seasonId?: string
): Promise<DashboardStats> => {
  const response = await apiClient.get<DashboardStats>("/api/stats/dashboard", {
    params: seasonId ? { seasonId } : undefined,
  });
  return response.data;
};

const monthFormat = new Intl.DateTimeFormat("en-GB", {
  month: "short",
  timeZone: "UTC",
  year: "2-digit",
});

export const formatMonth = (month: string): string => {
  const [year, monthIndex] = month.split("-").map(Number);
  return monthFormat.format(
    new Date(Date.UTC(year ?? 0, (monthIndex ?? 1) - 1))
  );
};

const dayFormat = new Intl.DateTimeFormat("en-GB", {
  day: "numeric",
  month: "short",
  timeZone: "Europe/London",
});

const dayYearFormat = new Intl.DateTimeFormat("en-GB", {
  day: "numeric",
  month: "short",
  timeZone: "Europe/London",
  year: "numeric",
});

const slotFormat = new Intl.DateTimeFormat("en-GB", {
  day: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  month: "short",
  timeZone: "Europe/London",
  weekday: "short",
});

export const formatSeasonRange = (startsAt: string, endsAt: string): string =>
  `${dayFormat.format(new Date(startsAt))} – ${dayYearFormat.format(new Date(endsAt))}`;

export const formatSlot = (iso: string): string =>
  slotFormat.format(new Date(iso));
